import { Formatters } from '@octano/global-ui/dist/utils/formatters';
import { SelectOptionType } from '@octano/global-ui';
import { isValidNumber } from 'libphonenumber-js';
import { clean, validate } from 'rut.js';
import validator from 'validator';

enum ValidationType {
  Min = 'min',
  Max = 'max',
  MaxDate = 'maxDate',
  Required = 'required',
  Pattern = 'pattern',
  MinLength = 'minLength',
  MaxLength = 'maxLength',
  Type = 'type',
}

export type FieldExtraFormatter =
  | 'uppercase'
  | 'uppercaseWithoutAccents'
  | 'noAccents';
export type FieldFormatter = keyof typeof Formatters | FieldExtraFormatter;

interface FieldProperties {
  visible?: boolean;
  readonly?: boolean;
  hidden?: boolean;
  disabled?: boolean;
  outlined?: boolean;
  maxLength?: number;
  formatter?: FieldFormatter;
  tooltip?: string;
  isMulti?: boolean;
}

interface ValidationRules {
  [ValidationType.Min]?: number;
  [ValidationType.Max]?: number;
  [ValidationType.MaxDate]?: Date;
  [ValidationType.Required]?: boolean;
  [ValidationType.Pattern]?: string;
  [ValidationType.MinLength]?: number;
  [ValidationType.MaxLength]?: number;
  [ValidationType.Type]?: 'rut' | 'phone' | 'email';
}

export interface ConditionalRule {
  field: string;
  operator: 'equals' | 'notEquals' | 'contains' | 'in' | 'notEmpty';
  value?: any;
}

export interface DependencyRule {
  parentField: string;
  optionKey: string;
  initiallyDisabled?: boolean;
}

export interface FormField {
  component: 'text' | 'select' | 'textarea' | 'date' | 'checkbox';
  name: string;
  resourceName?: string;
  optionKey?: string;
  options?: SelectOptionType[];
  label: string;
  placeholder?: string;
  dependsOn?: string[];
  conditional?: ConditionalRule;
  dependency?: DependencyRule;
  properties?: FieldProperties;
  validations?: ValidationRules;
  grid: {
    md: number;
    xs: number;
  };
}

export interface FormSection {
  title: string;
  tooltip?: {
    id: string;
    text: string;
  };
  source?: string | string[];
  component: string;
  conditional?: ConditionalRule;
  fields: FormField[];
}

type ValidationFunction = (value: any) => string | undefined;

function getRequiredValidation(): ValidationFunction {
  return (value: any) => {
    if (
      value === null ||
      value === undefined ||
      (typeof value === 'string' && value.trim() === '')
    ) {
      return 'Este campo es obligatorio';
    }
    return undefined;
  };
}

function getRutValidation(): ValidationFunction {
  return (value: any) => {
    return value && typeof value === 'string' && !validate(clean(value))
      ? 'El RUT debe tener formato de RUT'
      : undefined;
  };
}

function getPhoneValidation(): ValidationFunction {
  return (phone: any) => {
    if (!phone || typeof phone !== 'string' || phone.trim() === '') {
      return undefined;
    }
    return isValidNumber(phone.replaceAll(' ', ''))
      ? undefined
      : 'El teléfono debe tener formato +XXXXXXXXXXX';
  };
}

function getEmailValidation(): ValidationFunction {
  return (email: any) => {
    if (!email || typeof email !== 'string' || email.trim() === '') {
      return undefined;
    }
    return validator.isEmail(email)
      ? undefined
      : 'El correo debe tener formato de correo';
  };
}

function getArrayMinLengthValidation(minLength: number): ValidationFunction {
  return (selecteds: any) => {
    return selecteds &&
      Array.isArray(selecteds) &&
      selecteds.length >= minLength
      ? undefined
      : `La lista debe tener mínimo ${minLength}`;
  };
}

function getArrayMaxLengthValidation(maxLength: number): ValidationFunction {
  return (selecteds: any) => {
    return selecteds &&
      Array.isArray(selecteds) &&
      selecteds.length <= maxLength
      ? undefined
      : `La lista debe tener máximo ${maxLength}`;
  };
}

export function validationSchema(
  validations?: ValidationRules,
  options?: { isArray?: boolean },
) {
  const rules: Record<string, ValidationFunction> = {};
  if (validations) {
    for (const [key, value] of Object.entries(validations)) {
      switch (key as ValidationType) {
        case ValidationType.Required:
          if (options?.isArray) {
            rules[ValidationType.Required] = getArrayMinLengthValidation(1);
          } else {
            rules[ValidationType.Required] = getRequiredValidation();
          }
          break;
        case ValidationType.Type:
          switch (value as 'rut' | 'phone' | 'email') {
            case 'rut':
              rules[ValidationType.Type] = getRutValidation();
              break;
            case 'phone':
              rules[ValidationType.Type] = getPhoneValidation();
              break;
            case 'email':
              rules[ValidationType.Type] = getEmailValidation();
              break;
          }
          break;
        case ValidationType.MinLength:
          if (options?.isArray) {
            rules[ValidationType.MinLength] = getArrayMinLengthValidation(
              value,
            );
          }
          break;
        case ValidationType.MaxLength:
          if (options?.isArray) {
            rules[ValidationType.MaxLength] = getArrayMaxLengthValidation(
              value,
            );
          }
          break;
      }
    }
  }
  return Object.keys(rules).length === 0 ? undefined : { validate: rules };
}
